import React, { useState, useEffect } from 'react';
import { Box, Card, CardContent, CardMedia, Typography, Grid, Skeleton } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import type { Category } from '../types/blog';
import { getCategories } from '../utils/blogUtils';

const CategoryList: React.FC = () => {
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    const loadCategories = async () => {
      try {
        const data = await getCategories();
        setCategories(data);
        setLoading(false);
      } catch (err) {
        setError('Failed to load categories');
        setLoading(false);
      }
    };

    loadCategories();
  }, []);

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography color="error">{error}</Typography>
      </Box>
    );
  }

  if (loading) {
    return (
      <Box sx={{ width: '100%' }}>
        <Grid container spacing={3}>
          {[1, 2, 3].map((n) => (
            <Grid item key={n} xs={12} sm={6} md={4} sx={{ p: 1 }}>
              <Card>
                <Skeleton variant="rectangular" height={140} />
                <CardContent>
                  <Skeleton variant="text" height={24} width="60%" />
                  <Skeleton variant="text" height={18} width="90%" />
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%' }}>
      <Typography variant="h4" component="h1" sx={{ fontWeight: 500, letterSpacing: 0.5, mb: 0.5, color: 'text.secondary', textAlign: 'center', mt: 3 }}>
        Explore Categories
      </Typography>
      <Typography variant="subtitle1" color="text.secondary" sx={{ textAlign: 'center', mb: 3 }}>
        Browse blog posts by category
      </Typography>
      <Grid container spacing={3} sx={{ px: { xs: 0, md: 2 } }}>
        {categories.map((category) => (
          <Grid item key={category.id} xs={12} sm={6} md={4} sx={{ p: 1 }}>
            <Card
              sx={{
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                cursor: 'pointer',
                transition: 'transform 0.2s',
                '&:hover': {
                  transform: 'scale(1.02)',
                },
              }}
              onClick={() => navigate(`/category/${category.id}`)}
            >
              <CardMedia
                component="img"
                image={category.image}
                alt={category.name}
                sx={{
                  width: 80,
                  height: 80,
                  objectFit: 'contain',
                  mx: 'auto',
                  mt: 2,
                  mb: 1
                }}
              />
              <CardContent>
                <Typography gutterBottom variant="h5" component="div">
                  {category.name}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {category.description}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>
    </Box>
  );
};

export default CategoryList;